/**
 * sceneRuntimeGuard.ts — the anti-black-screen layer (2026-08-09 owner report).
 *
 * A user clicked Preview on an interactive deliverable and got a solid black
 * frame: nothing to interact with, nothing to read, no hint of what went
 * wrong. Two distinct failures produced the same dead rectangle:
 *
 *   1. `soma-social-sim.html` classified as kind `html` → rendered with
 *      `sandbox=""` — scripts stripped. A canvas app with its scripts removed
 *      paints exactly its static background: black. (Fixed in
 *      PreviewContent.tsx: scripted HTML now runs in the SAME opaque-origin
 *      sandbox the scene3d lane has always used — `allow-scripts` without
 *      `allow-same-origin`, so nothing ever executes in the app origin.)
 *
 *   2. A scene whose script throws at boot — external CDN import blocked or
 *      unreachable, bare-specifier module error, missing WebGL — dies before
 *      the first frame with the error visible only in a console the user
 *      will never open.
 *
 * Both are the same UX defect: FAILURE HAS NO FACE. This module gives it one.
 * `injectRuntimeGuard` prepends a tiny self-contained script that paints a
 * plain-language overlay when the content errors, fails to load a resource,
 * or never manages to draw — so the worst case degrades from "black mystery
 * rectangle" to "a sentence telling you what happened, and a Download button
 * that still works".
 *
 * The guard is inert on success: it waits, sees pixels were produced (a
 * canvas got a context, or meaningful DOM rendered), and deletes itself.
 */

/** True when the document would need scripts to show anything interactive. */
export function htmlNeedsScripts(html: string | null | undefined): boolean {
  return /<script[\s>]/i.test(String(html || ''));
}

/**
 * 2026-08-13 truncated-deliverable incident: a generated interactive-HTML file
 * arrived as a ~1KB head fragment — it opened like a full document but was cut
 * off before `</html>`, and previewed as a silent black rectangle. This flags
 * that signature so the shell can warn instead of staying mute.
 */
export function htmlLooksIncomplete(html: string | null | undefined): boolean {
  const src = String(html ?? '');
  if (!/^\s*(?:<!DOCTYPE\s+html|<html[\s>])/i.test(src)) return false;
  return !/<\/html\s*>/i.test(src);
}

/** External http(s) references a sandboxed srcdoc frame may fail to reach —
 *  surfaced in the overlay message so "blocked CDN" stops being a mystery. */
export function externalRefs(html: string | null | undefined): string[] {
  const out = new Set<string>();
  const re = /(?:src|href)\s*=\s*["'](https?:\/\/[^"']+)["']|import\s+[^'"]*["'](https?:\/\/[^"']+)["']/gi;
  for (const m of String(html || '').matchAll(re)) {
    try {
      out.add(new URL(m[1] || m[2]).hostname);
    } catch { /* unparseable URL — skip */ }
  }
  return [...out];
}

/**
 * The guard script. Constraints that shaped it:
 *  - Must be dependency-free and tiny (it ships inside every previewed doc).
 *  - Must NOT false-positive on a working scene → it only speaks when there
 *    was an actual error/failed load AND nothing meaningful rendered.
 *  - Must never break the content → wrapped in try/catch, touches the DOM
 *    only to add its own overlay element.
 */
function guardScript(externalHosts: string[]): string {
  const hostsNote = externalHosts.length
    ? ` This file references external services (${externalHosts.slice(0, 3).join(', ')}) that the sandboxed preview may not be allowed to reach.`
    : '';
  return `<script>(function(){
  try {
    var errs = [];
    function record(msg){ if (errs.length < 5) errs.push(String(msg || 'unknown error')); }
    window.addEventListener('error', function(e){
      if (e && e.target && (e.target.src || e.target.href)) { record('Failed to load: ' + (e.target.src || e.target.href)); }
      else { record(e && e.message); }
    }, true);
    window.addEventListener('unhandledrejection', function(e){ record(e && e.reason && (e.reason.message || e.reason)); });
    // R13 (2026-08-13 incident): the old success test counted ANY canvas with
    // width/height > 2 — but a bare <canvas> defaults to 300x150, so a dead
    // stub containing a canvas tag silenced the overlay for exactly the
    // black-rectangle failure this guard exists to explain. The guard runs
    // before any content script, so wrap getContext and count only canvases
    // whose script actually obtained a rendering context.
    var ctxCanvases = [], ctxTypes = [], ctxObjs = [];
    try {
      var origGetContext = HTMLCanvasElement.prototype.getContext;
      HTMLCanvasElement.prototype.getContext = function(type){
        var ctx = origGetContext.apply(this, arguments);
        if (ctx && ctxCanvases.indexOf(this) === -1) {
          ctxCanvases.push(this); ctxTypes.push(String(type || '')); ctxObjs.push(ctx);
        }
        return ctx;
      };
    } catch (_) { /* patch failure degrades to DOM-evidence only */ }
    function canvasPainted(c, type, ctx){
      // The app sized its canvas away from the 300x150 default → real boot.
      if (c.width !== 300 || c.height !== 150) return true;
      if (type === '2d') {
        // Default-size 2d canvas: only non-uniform pixels prove a draw.
        try {
          var d = ctx.getImageData(0, 0, Math.min(c.width, 32), Math.min(c.height, 32)).data;
          for (var i = 4; i < d.length; i += 4) {
            if (d[i] !== d[0] || d[i+1] !== d[1] || d[i+2] !== d[2] || d[i+3] !== d[3]) return true;
          }
        } catch (_) {}
        return false;
      }
      // webgl at default size: readPixels is unreliable without
      // preserveDrawingBuffer — context creation + error-free execution is
      // the best evidence available.
      return errs.length === 0;
    }
    function rendered(){
      try {
        for (var i = 0; i < ctxCanvases.length; i++) {
          if (canvasPainted(ctxCanvases[i], ctxTypes[i], ctxObjs[i])) return true;
        }
        var b = document.body;
        if (!b) return false;
        if ((b.innerText || '').replace(/\\s+/g, '').length > 40) return true;
        if (b.querySelector('img,svg,video,iframe,table,input,button')) return true;
        return false;
      } catch (_) { return false; }
    }
    function showOverlay(){
      if (document.getElementById('__pf_guard')) return;
      var d = document.createElement('div');
      d.id = '__pf_guard';
      d.style.cssText = 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(10,12,16,.92);color:#e6e9ef;font:14px/1.55 system-ui,sans-serif;z-index:2147483647;padding:24px;text-align:center;';
      var msg = 'This preview could not start.' + ${JSON.stringify(hostsNote)} + ' You can still download the file and open it directly in your browser.';
      var detail = errs.length ? '<div style="margin-top:12px;font-size:12px;color:#98a2b3;max-width:560px;word-break:break-word;">' + errs.join('<br>').replace(/</g,'&lt;') + '</div>' : '';
      d.innerHTML = '<div><div style="font-size:32px;margin-bottom:10px;">\\uD83D\\uDEA7</div><div style="max-width:480px;">' + msg + '</div>' + detail + '</div>';
      (document.body || document.documentElement).appendChild(d);
    }
    // Verdict pass: give the content a real chance to boot, then decide once.
    // Errors alone don't trigger it (a scene can throw in a hover handler and
    // still be perfectly usable) — only errors combined with a blank frame.
    var checks = 0;
    var t = setInterval(function(){
      checks += 1;
      if (rendered()) { clearInterval(t); return; }
      if ((errs.length > 0 && checks >= 4) || checks >= 16) { clearInterval(t); if (!rendered()) showOverlay(); }
    }, 500);
  } catch (_) { /* the guard must never take the content down */ }
})();</script>`;
}

/**
 * Prepend the guard to a document, before any of its own scripts run.
 * Idempotent-enough: guarded output is only ever fed to srcDoc, never
 * persisted, so double-injection cannot compound across sessions.
 */
export function injectRuntimeGuard(html: string | null | undefined): string {
  const src = String(html || '');
  const guard = guardScript(externalRefs(src));
  // After <head> when present (keeps the doctype/charset first), otherwise
  // before the first <script>, otherwise prepended outright.
  const headMatch = /<head[^>]*>/i.exec(src);
  if (headMatch) {
    const at = headMatch.index + headMatch[0].length;
    return `${src.slice(0, at)}\n${guard}${src.slice(at)}`;
  }
  const scriptMatch = /<script[\s>]/i.exec(src);
  if (scriptMatch) {
    return `${src.slice(0, scriptMatch.index)}${guard}\n${src.slice(scriptMatch.index)}`;
  }
  return `${guard}\n${src}`;
}
